import React, { useId } from 'react';
import KpSelect from '../ui/KpSelect';
import KpTextarea from '../ui/textarea/KpTextarea';
import ForslagFiler from './ForslagFiler';
import {
  TYPER, MODULER, ALLVARLIGHET, PAVERKAN, PLATSHALLARE, MAX_RUBRIK, MAX_BESKRIVNING, typFor,
} from './forslagRegler';

/**
 * ForslagFormular – ett formulär för buggar, förbättringar, nya funktioner och frågor (BL-169).
 * Används av ForslagPanel för alla tre ingångar (sidfot, hörnknapp, På gång).
 *
 * Props:
 *  - varden: { typKey, rubrik, beskrivning, modul, allvarlighet, paverkan }
 *  - satt(falt, varde)
 *  - filer, filfel, onLaggTillFiler(files), onTaBortFil(index)
 *  - fel?: { rubrik?: string }
 *  - autoModul: modulen som lästs ut ur adressen
 */
function Segment({ value, onChange, alternativ, ariaLabel }) {
  return (
    <div className="forslag-seg" role="radiogroup" aria-label={ariaLabel}>
      {alternativ.map((a) => (
        <button
          type="button"
          key={a.key}
          role="radio"
          aria-checked={value === a.key}
          className={`forslag-seg__btn${value === a.key ? ' is-active' : ''}`}
          onClick={() => onChange(a.key)}
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}

export default function ForslagFormular({ varden, satt, filer, filfel, onLaggTillFiler, onTaBortFil, fel = {}, autoModul }) {
  const rubrikId = useId();
  const beskrivningId = useId();
  const t = typFor(varden.typKey);
  const modulAlternativ = [{ value: '', label: autoModul ? `Ingen vald (${autoModul})` : 'Ingen vald' }, ...MODULER.map((m) => ({ value: m, label: m }))];

  return (
    <div className="forslag-form">
      <div className="forslag-falt">
        <span className="forslag-etikett" id={`${rubrikId}-typ`}>Vad gäller det?</span>
        <Segment value={varden.typKey} onChange={(v) => satt('typKey', v)} alternativ={TYPER} ariaLabel="Typ av ärende" />
      </div>

      <div className="forslag-falt">
        <label className="forslag-etikett" htmlFor={rubrikId}>Rubrik</label>
        <input
          id={rubrikId}
          className={`forslag-input${fel.rubrik ? ' forslag-input--fel' : ''}`}
          type="text"
          value={varden.rubrik}
          maxLength={MAX_RUBRIK}
          placeholder="En kort mening om vad det gäller"
          aria-invalid={fel.rubrik ? 'true' : undefined}
          aria-describedby={fel.rubrik ? `${rubrikId}-fel` : undefined}
          onChange={(e) => satt('rubrik', e.target.value)}
        />
        <div className="forslag-rad">
          {fel.rubrik ? <span className="forslag-fel" id={`${rubrikId}-fel`}>{fel.rubrik}</span> : <span />}
          <span className="forslag-raknare">{varden.rubrik.length} / {MAX_RUBRIK}</span>
        </div>
      </div>

      <div className="forslag-falt">
        <label className="forslag-etikett" htmlFor={beskrivningId}>Beskrivning <span className="forslag-valfritt">valfritt</span></label>
        <KpTextarea
          id={beskrivningId}
          value={varden.beskrivning}
          onChange={(v) => satt('beskrivning', v)}
          maxLength={MAX_BESKRIVNING}
          placeholder={PLATSHALLARE[varden.typKey]}
          rows={5}
        />
      </div>

      {t.typ === 'bug' && (
        <div className="forslag-falt">
          <span className="forslag-etikett">Hur allvarligt är det? <span className="forslag-valfritt">valfritt</span></span>
          <Segment value={varden.allvarlighet} onChange={(v) => satt('allvarlighet', v)} alternativ={ALLVARLIGHET} ariaLabel="Allvarlighetsgrad" />
        </div>
      )}

      {t.typ === 'improvement' && (
        <div className="forslag-falt">
          <span className="forslag-etikett">Hur många berörs? <span className="forslag-valfritt">valfritt</span></span>
          <Segment value={varden.paverkan} onChange={(v) => satt('paverkan', v)} alternativ={PAVERKAN} ariaLabel="Hur många berörs" />
        </div>
      )}

      <div className="forslag-falt">
        <span className="forslag-etikett">Modul <span className="forslag-valfritt">valfritt</span></span>
        <KpSelect value={varden.modul} onChange={(v) => satt('modul', v)} options={modulAlternativ} placeholder="Ingen vald" />
      </div>

      <div className="forslag-falt">
        <span className="forslag-etikett">Filer <span className="forslag-valfritt">valfritt</span></span>
        <ForslagFiler filer={filer} fel={filfel} onLaggTill={onLaggTillFiler} onTaBort={onTaBortFil} />
      </div>

      <p className="forslag-info">
        Sidan du är på, webbläsare, skärmstorlek och appversion skickas med automatiskt. Lösenord och inloggningsuppgifter skickas aldrig.
      </p>
    </div>
  );
}
